/**
 * OWA-1978: the one check behind every way of deleting a task. A finalized invoice is immutable, and it
 * can list a task whose own invoiceId is empty, so the check reads the invoices as well as the task.
 *
 * The check walks the store. A menu calls it from showIf, which runs only while the menu is open; never
 * call it while rendering a row.
 */
import {isTaskLockedByFinalizedInvoice, type InvoiceLookup} from '@/Financial/Invoices/finalizedLock';
import {blockedReason, CANNOT_DELETE_TITLE, combineImpacts, confirmCascadeDelete, isBlocker} from '@/lib/cascadeDelete';
import type Task from '@/entities/task';
import type {DeleteImpact} from '@/store/deps/deleteContext';
import {deleteImpactTask} from '@/store/deps/taskDeps';
import store from '@/store/store';
// A namespace import, so a test that mocks '@/ui/Confirm' without every dialog still loads this module.
import * as Confirm from '@/ui/Confirm';
import type {ReactElement} from 'react';

type TaskDeletion<Message> = {canDelete: boolean; message: Message | string};

/**
 * Keep the invoice restriction ahead of the existing delete-impact calculation. The Associated Tasks table
 * runs this while it renders, so a check that throws refuses the delete instead of reaching the error boundary.
 */
export function taskDeletion<Message extends string | ReactElement>(
    task: Pick<Task, 'id' | 'invoiceId'>,
    invoices: InvoiceLookup,
    deleteImpact: () => {canDelete: boolean; message?: Message},
): TaskDeletion<Message> {
    try {
        if (task.invoiceId) {
            const invoice = invoices.get(task.invoiceId);
            if (invoice && invoice.status !== 'D') return {
                canDelete: false,
                message: `Associated with invoice ${invoice.invoiceNumber} [${invoice.id}]`,
            };
        }
        const finalizedInvoice = invoices.values().find(invoice => isTaskLockedByFinalizedInvoice(task, {
            get: id => id === invoice.id ? invoice : undefined,
            values: () => [invoice],
        }));
        if (finalizedInvoice) return {
            canDelete: false,
            message: `Associated with invoice ${finalizedInvoice.invoiceNumber} [${finalizedInvoice.id}]`,
        };
        const impact = deleteImpact();
        return {canDelete: impact.canDelete, message: impact.canDelete ? '' : impact.message ?? ''};
    }
    catch (err) {
        console.error(err);
        return {canDelete: false, message: 'Error occurred calculating delete impact.'};
    }
}

/** The check against the store, with the impact counts for the confirmation. */
function checkTask(task: Task): TaskDeletion<string> & {impacts: DeleteImpact} {
    let impacts: DeleteImpact = [];
    const decision = taskDeletion<string>(task, store.getState().invoices, () => {
        impacts = deleteImpactTask(task);
        const canDelete = !impacts.some(isBlocker);
        return {canDelete, message: canDelete ? undefined : blockedReason('task', impacts)};
    });
    return {...decision, impacts};
}

/** For a menu's showIf: only while the menu is open. */
export function isTaskDeletable(task: Task): boolean {
    return checkTask(task).canDelete;
}

/** Why this task cannot be deleted, naming the task, or null when it can. A selection or a cancelled record holds several. */
export function taskDeleteRefusal(task: Task): {impacts: DeleteImpact; message: string} | null {
    const {canDelete, impacts, message} = checkTask(task);
    if (canDelete) return null;
    return {impacts, message: task.title ? `${task.title}: ${message}` : message};
}

/** The refusal dialog: a single OK button and no Yes. */
export function showTaskRefusal(message: string): void {
    Confirm.confirmWarning(CANNOT_DELETE_TITLE, message, 'OK', () => {});
}

/** Show why the first refused task cannot be deleted. Returns true when one was refused. */
export function refuseTaskDelete(tasks: readonly Task[]): boolean {
    const refusal = tasks.map(taskDeleteRefusal).find(result => result !== null);
    if (refusal) showTaskRefusal(refusal.message);
    return !!refusal;
}

/**
 * Ask before deleting these tasks, or refuse every one of them when any is refused. An invoice can be
 * finalized while the question is open, so Yes checks again. Returns true when the question was asked.
 */
export function confirmTaskDelete(options: Omit<Parameters<typeof confirmCascadeDelete>[0], 'entityLabel' | 'impacts'> & {
    tasks: readonly Task[],
}): boolean {
    const {tasks, onYes, ...question} = options;
    if (refuseTaskDelete(tasks)) return false;
    return confirmCascadeDelete({
        ...question,
        entityLabel: 'task',
        impacts: combineImpacts(tasks.map(task => checkTask(task).impacts)),
        onYes: e => {
            if (!refuseTaskDelete(tasks)) onYes(e);
        },
    });
}
